// src/features/weekly-one/weeklyOneUtils.ts

export type CardState = 'empty' | 'active' | 'final-day' | 'wrap' | 'completed';

export interface WeeklyCommitment {
  id: string;
  user_id: string;
  week_start: string;   // 'YYYY-MM-DD'
  week_end: string;     // 'YYYY-MM-DD'
  commitment_text: string;
  offers_submitted_last_week: number;
  blocker_text: string | null;
  status: 'active' | 'completed' | 'missed' | 'partial';
  created_at: string;
  completed_at: string | null;
  updated_at: string;
}

// ---------------------------------------------------------------------------
// Internal: parse a Date into Central Time calendar components.
// ---------------------------------------------------------------------------
function getCTComponents(date: Date): { year: number; month: number; day: number; dayOfWeek: number } {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    weekday: 'short',
  }).formatToParts(date);

  const get = (type: string) => parts.find(p => p.type === type)?.value ?? '';
  const weekdayMap: Record<string, number> = {
    Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6,
  };

  return {
    year:      parseInt(get('year')),
    month:     parseInt(get('month')),
    day:       parseInt(get('day')),
    dayOfWeek: weekdayMap[get('weekday')] ?? 1,
  };
}

function toYMD(d: Date): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'UTC',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);
  const get = (t: string) => parts.find(p => p.type === t)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}`;
}

/** Returns 'YYYY-MM-DD' for the Monday that starts the current CT week. */
export function getCTWeekStart(date: Date = new Date()): string {
  const { year, month, day, dayOfWeek } = getCTComponents(date);
  const daysFromMon = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
  const monday = new Date(Date.UTC(year, month - 1, day - daysFromMon));
  return toYMD(monday);
}

/** Returns 'YYYY-MM-DD' for the Sunday that ends the current CT week. */
export function getCTWeekEnd(date: Date = new Date()): string {
  const start = getCTWeekStart(date);
  const [y, m, d] = start.split('-').map(Number);
  const sunday = new Date(Date.UTC(y, m - 1, d + 6));
  return toYMD(sunday);
}

/** Returns the number of full days remaining until Sunday 11:59pm CT cutoff.
 *  0 = today is Sunday (cutoff day), 6 = today is Monday. */
export function getDaysUntilCutoff(date: Date = new Date()): number {
  const { dayOfWeek } = getCTComponents(date);
  return dayOfWeek === 0 ? 0 : 7 - dayOfWeek;
}

/** True if today is Sunday in Central Time. */
export function isFinalDay(date: Date = new Date()): boolean {
  return getCTComponents(date).dayOfWeek === 0;
}

/** True if today is Monday in Central Time. */
export function isFirstDay(date: Date = new Date()): boolean {
  return getCTComponents(date).dayOfWeek === 1;
}

/** True if today is Wednesday or later (Wed-Sun) in CT.
 *  Used to show late-week urgency text in the empty state. */
export function isLateWeek(date: Date = new Date()): boolean {
  const { dayOfWeek } = getCTComponents(date);
  return dayOfWeek === 0 || dayOfWeek >= 3;
}

/**
 * Derives which of the four card states to show.
 *
 *  'wrap'       - Monday, last week completed, no current commitment yet
 *  'empty'      - no active commitment this week
 *  'final-day'  - Sunday, commitment set but not complete
 *  'active'     - commitment set, not Sunday
 */
export function deriveCardState(
  current: WeeklyCommitment | null,
  lastWeek: WeeklyCommitment | null,
  date: Date = new Date(),
): CardState {
  const monday = isFirstDay(date);
  const sunday = isFinalDay(date);

  if (monday && lastWeek?.status === 'completed' && !current) return 'wrap';
  if (!current || current.status === 'missed' || current.status === 'partial') return 'empty';
  if (current.status === 'completed') return 'completed';
  if (sunday && current.status === 'active') return 'final-day';
  if (current.status === 'active') return 'active';
  return 'empty';
}
